import React, { useState, useContext } from 'react'
import { css } from '@emotion/core'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Button, ButtonType } from '@/components/atoms/button'
import { sectionHeaderStyle, sectionStyle } from '@/components/organisms/dashboard/style'
import { setHasAccountingInterlockingPlugin } from '@/libs/plugins'
import { AccountContext } from '@/contexts/account'
import * as api from '@/apis/aipass'
import { Popup } from '@/components/atoms/popup'
import { downloadInline } from '@/libs/csv-download'
import { DailySummary, MonthlySummary } from '@/models/sales-manager/detail'
import { useSalesExportRequest } from '@/hooks/use-sales-export-request'
import { SalesExportFormat, SalesExportType } from '@/models/sales-manager/sales-export'

type DetailHeaderProps = {
  salesTotalDetail: DailySummary | MonthlySummary
  mode: 'month' | 'daily'
}

export const DetailHeader: React.FC<DetailHeaderProps> = props => {
  const { t } = useTranslation()
  const { plugins } = useContext<any>(AccountContext)
  const { requestExport } = useSalesExportRequest()

  const hasAccountingInterlockingPlugin = setHasAccountingInterlockingPlugin(plugins)

  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false)

  const isUnpaid = props.salesTotalDetail.indicator.unpaidTotal > 0
  const isOutsidePeriodToClosing = !props.salesTotalDetail.payments.summaryFrom && !props.salesTotalDetail.payments.summaryTo

  const salesDetailListTo = {
    pathname: '/sales-manager/detail-list',
    search: new URLSearchParams({
      beginDate: props.salesTotalDetail.sales.summaryFrom,
      endDate: props.salesTotalDetail.sales.summaryTo,
    }).toString(),
  }

  const exportRevAndCl = async () => {
    try {
      const [rev, cl] = await Promise.all([
        api.fetchOldComExportRevCsv(props.salesTotalDetail.sales.summaryFrom, 'REV'),
        api.fetchOldComExportRevCsv(props.salesTotalDetail.sales.summaryFrom, 'CL'),
      ])
      downloadInline(rev.url)
      await new Promise(resolve => setTimeout(resolve, 500))
      downloadInline(cl.url)
    } catch (e) {
      console.error(e)
    }
  }

  const downloadSaleDetails = () =>
    requestExport(() => api.requestSalesDetailExport(props.salesTotalDetail.sales.summaryFrom, props.salesTotalDetail.sales.summaryTo))

  const downloadPaymentList = () =>
    requestExport(() =>
      api.requestSalesExport({
        type: SalesExportType.PaymentList,
        format: 'csv',
        from: props.salesTotalDetail.sales.summaryFrom,
        to: props.salesTotalDetail.sales.summaryTo,
      }),
    )

  const downloadDailyClosing = (format: SalesExportFormat) =>
    requestExport(() =>
      api.requestSalesExport({
        type: SalesExportType.DailyClosing,
        format,
        // 営業日は backend が YYYY-MM-DD で返す（入金一覧の from / to と同じ値）
        date: props.salesTotalDetail.sales.summaryFrom,
      }),
    )

  const downloadMonthlyClosing = (format: SalesExportFormat) =>
    requestExport(() =>
      api.requestSalesExport({
        type: SalesExportType.MonthlyClosing,
        format,
        month: props.salesTotalDetail.sales.summaryFrom,
      }),
    )

  return (
    <div css={detailSectionStyle}>
      <div css={detailHeaderStyle}>
        <div css={detailHeaderDateWrapperStyle}>
          <p css={detailHeaderDateStyle}>{props.mode === 'daily' ? t('Daily closing state') : t('Monthly closing state')}</p>
          <p style={{ color: '#F2A40B', fontSize: 16, letterSpacing: 0.7 }}>
            {props.mode === 'daily'
              ? `${t('Business day')}：${dayjs(props.salesTotalDetail.sales.summaryFrom).format(t('YYYY-M-D'))}`
              : `${t('Business month')}：${dayjs(props.salesTotalDetail.sales.summaryFrom).format(t('YYYY-M'))}`}
          </p>
          {props.salesTotalDetail.isConfirm && <p css={isConfirmStyle}>{t('Confirmed')}</p>}
        </div>
        <div css={rightContentHeaderWrapperStyle}>
          <div css={rightButtonWrapperStyle}>
            <Button
              buttonType={4}
              width={156}
              height={32}
              fontSize={12}
              marginRight={16}
              icon={require('@/static/images/csv.svg')}
              onClick={() => setIsDropDownOpen(!isDropDownOpen)}
            >
              {t('Download')} <img src={require('@/static/images/select_gray.svg')} css={dropDownIcon} />
            </Button>
            <Popup isOpen={isDropDownOpen} onClose={() => setIsDropDownOpen(false)} customStyle={css({ width: 168, left: 0, top: 32 })}>
              <ul>
                <li
                  css={dropDownContentStyle}
                  onClick={() => {
                    downloadSaleDetails()
                    setIsDropDownOpen(false)
                  }}
                >
                  <p css={dropDownTitleStyle}>{t('SaleDetail')}</p>
                </li>
                <li
                  css={isOutsidePeriodToClosing ? disabledButton : dropDownContentStyle}
                  onClick={() => {
                    downloadPaymentList()
                    setIsDropDownOpen(false)
                  }}
                >
                  <p css={dropDownTitleStyle}>{t('Payment list')}</p>
                </li>
                {props.mode === 'daily' && (
                  <>
                    <li
                      css={dropDownContentStyle}
                      onClick={() => {
                        downloadDailyClosing('pdf')
                        setIsDropDownOpen(false)
                      }}
                    >
                      <p css={dropDownTitleStyle}>{`${t('Export daily closing')}(PDF)`}</p>
                    </li>
                    <li
                      css={dropDownContentStyle}
                      onClick={() => {
                        downloadDailyClosing('csv')
                        setIsDropDownOpen(false)
                      }}
                    >
                      <p css={dropDownTitleStyle}>{`${t('Export daily closing')}(CSV)`}</p>
                    </li>
                  </>
                )}
                {props.mode === 'month' && (
                  <>
                    <li
                      css={dropDownContentStyle}
                      onClick={() => {
                        downloadMonthlyClosing('pdf')
                        setIsDropDownOpen(false)
                      }}
                    >
                      <p css={dropDownTitleStyle}>{`${t('Export monthly closing')}(PDF)`}</p>
                    </li>
                    <li
                      css={dropDownContentStyle}
                      onClick={() => {
                        downloadMonthlyClosing('csv')
                        setIsDropDownOpen(false)
                      }}
                    >
                      <p css={dropDownTitleStyle}>{`${t('Export monthly closing')}(CSV)`}</p>
                    </li>
                  </>
                )}
                {hasAccountingInterlockingPlugin && props.mode === 'daily' && (
                  <li
                    css={!props.salesTotalDetail.isConfirm ? disabledButton : dropDownContentStyle}
                    onClick={() => {
                      exportRevAndCl()
                      setIsDropDownOpen(false)
                    }}
                  >
                    <p css={!props.salesTotalDetail.isConfirm ? disabledTitle : dropDownTitleStyle}>{t('REV CL')}</p>
                  </li>
                )}
              </ul>
            </Popup>
          </div>
          <Link to={salesDetailListTo} css={detailLinkButtonStyle}>
            {t('Detail')}
          </Link>
        </div>
      </div>
      <ul css={contentsWrapperStyle}>
        <li css={contentsStyle}>
          <div css={contentsTitleStyle}>{t('Actual sales')}</div>
          <div css={contentsMoneyStyle}>¥{props.salesTotalDetail.indicator.salesTotal.toLocaleString()}</div>
        </li>
        <li css={contentsStyle}>
          <div css={isUnpaid ? contentsReceivableTitleStyle : contentsTitleStyle}>
            {t(props.mode === 'daily' ? 'Today’s Receivables' : 'Monthly Receivables')}
          </div>
          <div css={isUnpaid ? contentsReceivableMoneyStyle : contentsMoneyStyle}>
            ¥{props.salesTotalDetail.indicator.unpaidTotal.toLocaleString()}
          </div>
          <div style={{ textAlign: 'right', paddingTop: 8, fontSize: 14, letterSpacing: 1.3 }}>
            ({t('Post-Next Biz Day CO')}:¥{props.salesTotalDetail.indicator.carryoverPriceTotal.toLocaleString()})
          </div>
        </li>
        <li css={contentsStyle}>
          <div css={contentsTitleStyle}>{t(props.mode === 'daily' ? 'Today’s Deposits' : 'Monthly Deposits')}</div>
          <div css={contentsMoneyStyle}>¥{props.salesTotalDetail.indicator.depositTotal.toLocaleString()}</div>
          <div style={{ textAlign: 'right', paddingTop: 8, fontSize: 14, letterSpacing: 1.3 }}>
            ({t('Deposit balance')}:¥{props.salesTotalDetail.indicator.depositBalance.toLocaleString()})
          </div>
        </li>
      </ul>
    </div>
  )
}

const detailSectionStyle = css(sectionStyle, {
  width: '100%',
  background: '#FFFFFF',
  marginBottom: 16,
})

const detailHeaderStyle = css(sectionHeaderStyle, {
  height: 50,
  fontSize: 18,
  justifyContent: 'space-between',
  margin: '0 32px',
  padding: 0,
})

const detailHeaderDateWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

const detailHeaderDateStyle = css({
  fontSize: 18,
})

const isConfirmStyle = css({
  width: 75,
  height: 25,
  border: '1px solid #676767',
  borderRadius: 5,
  color: '#676767',
  fontSize: 12,
  lineHeight: '25px',
  textAlign: 'center',
  letterSpacing: 1.8,
})

const contentsWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  borderTop: '1px solid #F2F2F2',
})

const contentsStyle = css({
  height: 120,
  width: '33.3%',
  padding: '0 32px',
  borderRight: '1px solid #F2F2F2',
  ':last-child': {
    borderRight: 'none',
  },
})

const contentsTitleStyle = css({
  fontSize: 16,
  letterSpacing: 2.4,
  fontWeight: 'bold',
  margin: '24px 0 0 0',
})
const contentsMoneyStyle = css({
  fontSize: 18,
  letterSpacing: 2.7,
  fontWeight: 'bold',
  margin: '16px 0 0 auto',
  width: 'max-content',
})
const contentsReceivableTitleStyle = css(contentsTitleStyle, {
  color: '#F2A40B',
})
const contentsReceivableMoneyStyle = css(contentsMoneyStyle, {
  color: '#F2A40B',
})

const rightContentHeaderWrapperStyle = css({
  display: 'flex',
})

const rightButtonWrapperStyle = css({
  display: 'flex',
  position: 'relative',
})

const dropDownIcon = css({
  marginLeft: 8,
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 19px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f2f2f2',
  },
})

const disabledButton = css(dropDownContentStyle, {
  pointerEvents: 'none',
})

const dropDownTitleStyle = css({
  fontSize: 16,
  color: '#272727',
  letterSpacing: '1.6px',
})

const disabledTitle = css(dropDownTitleStyle, {
  color: '#cccccc',
})

const detailLinkButtonStyle = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 74,
  height: 32,
  fontSize: 12,
  fontWeight: 'bold',
  borderRadius: 24,
  textDecoration: 'none',
  color: '#f2a40b',
  border: '1px solid #f2a40b',
  background: '#FFF',
  boxSizing: 'border-box',
  cursor: 'pointer',
  '&:hover': {
    color: '#FFF',
    borderColor: '#F2A40B',
    background: '#F2A40B',
  },
})
